// @ts-strict-ignore
import {
  AssigneeInputBlock,
  CardBlock,
  CardsInputBlock,
  DateInputBlock,
  DateRangeBlock,
  FieldBlock,
  FileInputBlock as FileInputBlockType,
  ImageBlock,
  InputBlock,
  NumberInputBlock,
  RecurrenceInputBlock,
  ReminderInputBlock,
  SubformsInputBlock as SubformsInputBlockType,
  TagsBlock,
  TagsInputBlock,
  TodoInputBlock,
  evaluateSelector,
} from '@coast/schemas';
import { isEmpty, isNil, sum } from 'lodash';
import { ReactElement } from 'react';
import { View } from '@shared/components/View';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { NoCodeBlockContainer } from './NoCodeBlockContainer';
import { RenderContext } from './contexts/render';
import { MBButtonBlock } from './lib/button';
import { MBCardsBlock } from './lib/cards';
import { MBFieldChannelBlock } from './lib/channel';
import { MBChatThreadBlock } from './lib/chatThread';
import { MBChatThreadInputBlock } from './lib/chatThreadInput';
import { MBDateRangeBlock } from './lib/dateRange';
import { MBFieldBlock } from './lib/field';
import { MBFieldAssigneeBlock } from './lib/fieldAssignee';
import { MBFieldFileBlock } from './lib/fieldFile';
import { SubformsFieldBlock } from './lib/fieldSubforms/SubformsFieldBlock';
import { MBImageBlock } from './lib/image';
import { MBInCardsBlock } from './lib/inCards';
import { MBCardInputBlockAddress } from './lib/inputAddress';
import { MBCardInputBlockAssignee } from './lib/inputAssignee';
import { MBCardInputBlockCards } from './lib/inputCards/MBCardInputBlockCards';
import { CardInputBlockDate } from './lib/inputDate';
import { MBCardInputBlockDateRange } from './lib/inputDateRange';
import { FileInputBlock } from './lib/inputFile/FileInputBlock';
import { MBCardInputBlockGeolocation } from './lib/inputGeolocation';
import { MBCardInputBlockImage } from './lib/inputImage';
import { MBCardInputBlockNumber } from './lib/inputNumber';
import { MBCardInputBlockQuestion } from './lib/inputQuestion';
import { MBCardInputBlockRecurrence } from './lib/inputRecurrence';
import { MBCardInputBlockReminder } from './lib/inputReminder';
import { SignatureInputBlock } from './lib/inputSignature/SignatureInputBlock';
import { SubformsInputBlock } from './lib/inputSubforms/SubformsInputBlock';
import { MBCardInputBlockTags } from './lib/inputTags';
import { MBCardInputBlockText } from './lib/inputText';
import { MBCardInputBlockTextarea } from './lib/inputTextarea';
import { MBCardInputBlockTodo } from './lib/inputTodo';
import { MBCardInputBlockToggle } from './lib/inputToggle';
import { MBSeenByBlock } from './lib/seenBy';
import { MBSequenceNumberBlock } from './lib/sequenceNumber';
import { MBTagsBlock } from './lib/tags';
import { CommonFields, Fields, Reminder, TodoItem } from './types';
import { defaultEvaluationContext } from './utils/tokens';

function getFieldValue(fields: Fields, block: FieldBlock | ImageBlock | InputBlock) {
  return fields[block.name];
}

function nonRedactedBlocks(card: API.CardFragment) {
  return (block: CardBlock) => {
    if (card.planLimitImposed) {
      return block.type === 'field' && block.name === 'name';
    }
    return true;
  };
}

function nonEmptyBlocks(
  fields: Fields,
  canReadThread: boolean,
  canCreateThread: boolean,
  canUpdateCard: boolean,
  printing: boolean,
) {
  return (block: CardBlock) => {
    switch (block.type) {
      case 'image':
        return !!fields[block.name];
      case 'field':
        if (
          block.fieldType === 'questionCheckbox' ||
          block.fieldType === 'questionTextArea' ||
          block.fieldType === 'questionSingleSelect'
        ) {
          return true;
        }
        if (block.fieldType === 'toggle' || block.fieldType === 'channel') {
          return true;
        }
        if (block.fieldType === 'todo') {
          const todos: TodoItem[] = fields[block.name];
          return !isEmpty(todos);
        }
        if (block.fieldType === 'reminder') {
          const reminders: Reminder[] = fields[block.name];
          return !isEmpty(reminders);
        }
        return ![null, undefined, ''].includes(fields[block.name]);
      case 'seen-by':
        return !isNil(fields.threadId) && !printing;
      case 'chat-thread':
        return !isNil(fields.threadId) && !printing && canReadThread;
      case 'chat-thread-input':
        return !isNil(fields.threadId) && !printing && canCreateThread;
      case 'date-range':
        const startDateFieldName = block.startDateFieldName ?? 'startDate';
        const endDateFieldName = block.endDateFieldName ?? 'endDate';
        return !isNil(fields[startDateFieldName]) && !isNil(fields[endDateFieldName]);
      case 'button':
        return canUpdateCard && !printing;
      case 'sequenceNumber':
        return !isNil(fields.sequenceNumber);
      case 'input':
      default:
        return true;
    }
  };
}

export function InputBlockComponent({
  block,
  onFieldPress,
  value,
  workflowTemplateId,
}: {
  block: InputBlock;
  onFieldPress: () => void;
  value: any;
  workflowTemplateId?: string;
}) {
  switch (block.fieldType) {
    case 'date':
    case 'time':
    case 'datetime':
      return (
        <CardInputBlockDate
          key={block.name}
          block={block as DateInputBlock}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'geolocation':
      return <MBCardInputBlockGeolocation key={block.name} block={block} />;
    case 'address':
      return <MBCardInputBlockAddress key={block.name} block={block} />;
    case 'file':
      return <FileInputBlock key={block.name} block={block as FileInputBlockType} />;
    case 'image':
      return <MBCardInputBlockImage key={block.name} block={block} />;
    case 'text':
      return <MBCardInputBlockText key={block.name} block={block} />;
    case 'questionCheckbox':
    case 'questionTextArea':
    case 'questionSingleSelect':
      return <MBCardInputBlockQuestion key={block.name} block={block} />;
    case 'float':
    case 'percent':
    case 'currency':
      return <MBCardInputBlockNumber key={block.name} block={block as NumberInputBlock} />;
    case 'textarea':
      return <MBCardInputBlockTextarea key={block.name} block={block} />;
    case 'todo':
      return (
        <MBCardInputBlockTodo
          key={block.name}
          block={block as TodoInputBlock}
          onPress={onFieldPress}
        />
      );
    case 'assignee':
      return <MBCardInputBlockAssignee key={block.name} block={block as AssigneeInputBlock} />;
    case 'toggle':
      return <MBCardInputBlockToggle key={block.name} block={block} />;
    case 'recurrence':
      return <MBCardInputBlockRecurrence key={block.name} block={block as RecurrenceInputBlock} />;
    case 'reminder':
      return <MBCardInputBlockReminder key={block.name} block={block as ReminderInputBlock} />;
    case 'tags':
      return <MBCardInputBlockTags key={block.name} block={block as TagsInputBlock} />;
    case 'cards':
      return <MBCardInputBlockCards key={block.name} block={block as CardsInputBlock} />;
    case 'signature':
      return <SignatureInputBlock key={block.name} block={block} />;
    case 'subforms':
      return (
        <SubformsInputBlock
          key={block.name}
          block={block as SubformsInputBlockType}
          value={value}
        />
      );
    default:
      return null;
  }
}

export function renderFieldBlock(
  block: FieldBlock,
  key: any,
  value: any,
  onFieldPress: () => void,
): ReactElement {
  switch (block.fieldType) {
    case 'channel':
      return <MBFieldChannelBlock key={key} block={block} onPress={onFieldPress} />;
    case 'assignee':
      if (!value) {
        return <View key={key} />;
      }
      return <MBFieldAssigneeBlock key={key} block={block} value={value} onPress={onFieldPress} />;
    case 'file':
      if (!value?.length) {
        return <View key={key} />;
      }
      return <MBFieldFileBlock key={key} value={value} label={block.label} />;
    case 'questionTextArea':
    case 'questionCheckbox':
    case 'questionSingleSelect':
      /*
      Render a question input block in completion mode on the details (and read-only)
      views so users can complete questions without having to navigate to the edit form.
      We may choose to change how this works in the future.
       */
      return (
        <MBCardInputBlockQuestion
          block={{ ...block, mode: 'completion' } as any as InputBlock}
          key={key}
        />
      );
    case 'subforms':
      return <SubformsFieldBlock key={key} block={block} value={value} />;
    default:
      return <MBFieldBlock key={key} block={block} value={value} onPress={onFieldPress} />;
  }
}

export function renderBlockIfNotEmpty(
  block: CardBlock,
  card: API.CardFragment,
  fields: CommonFields & Fields,
  canReadThread: boolean,
  canCreateThread: boolean,
  canUpdateCard: boolean,
  printing: boolean,
  onFieldPress?: () => void,
): ReactElement {
  const isBlockEmpty = !nonEmptyBlocks(
    fields,
    canReadThread,
    canCreateThread,
    canUpdateCard,
    printing,
  )(block);

  if (isBlockEmpty) {
    return null;
  }
  return renderBlock(block, card, fields, onFieldPress, 0);
}

export function renderBlock(
  block: CardBlock,
  card: API.CardFragment,
  fields: CommonFields & Fields,
  onFieldPress: VoidFunction | undefined,
  position: number,
  workflowTemplateId?: string,
): ReactElement {
  switch (block.type) {
    case 'sequenceNumber':
      return <MBSequenceNumberBlock key={position} sequenceNumber={card.sequenceNumber} />;
    case 'cards':
      return (
        <MBCardsBlock
          key={position}
          block={block}
          onPress={onFieldPress}
          relatedCards={card.relatedCards}
        />
      );
    case 'in-cards':
      return (
        <MBInCardsBlock key={position} block={block} onPress={onFieldPress} cardId={card.id} />
      );
    case 'tags':
      return <MBTagsBlock key={position} block={block} onPress={onFieldPress} />;
    case 'button':
      return <MBButtonBlock key={position} block={block} card={card} />;
    case 'input':
      return (
        <InputBlockComponent
          key={position}
          block={block}
          onFieldPress={onFieldPress}
          value={getFieldValue(fields, block)}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'date-range-input':
      return <MBCardInputBlockDateRange key={position} block={block} />;
    case 'date-range':
      const startDateFieldName = block.startDateFieldName ?? 'startDate';
      const endDateFieldName = block.endDateFieldName ?? 'endDate';
      return (
        <MBDateRangeBlock
          key={position}
          startDate={fields[startDateFieldName]}
          endDate={fields[endDateFieldName]}
          block={block}
          onPress={onFieldPress}
        />
      );
    case 'image':
      return <MBImageBlock key={position} url={getFieldValue(fields, block)} />;
    case 'field':
      return renderFieldBlock(block, position, getFieldValue(fields, block), onFieldPress);
    case 'seen-by':
      // Note(fant): this assumes that there's always a chat thread block
      // where this is used. Otherwise we have no way of knowing what
      // users have seen a card details.
      return <MBSeenByBlock key={position} threadId={(card as API.CardFragment).threadId} />;
    case 'chat-thread':
      return <MBChatThreadBlock key={position} threadId={(card as API.CardFragment).threadId} />;
    case 'chat-thread-input':
      return <MBChatThreadInputBlock key={position} cardId={card.id} />;
    case 'no-code':
      return (
        <NoCodeBlockContainer
          key={position}
          componentId={block.componentId}
          fields={fields}
          workflowTemplateId={card.workflowTemplateId ?? workflowTemplateId}
        />
      );
    default:
      return null;
  }
}

export function getBlocksToRender(
  blocks: CardBlock[],
  card: API.CardFragment,
  fields: CommonFields & Fields,
  canReadThread: boolean,
  canCreateThread: boolean,
  canUpdateCard: boolean,
  printing: boolean,
) {
  const filters: Array<(block: CardBlock) => boolean> = [
    nonRedactedBlocks(card),
    nonEmptyBlocks(fields, canReadThread, canCreateThread, canUpdateCard, printing),
    block => evaluateSelector(block.selector, fields, defaultEvaluationContext),
  ];

  return blocks.filter(block => filters.every(ff => ff(block)));
}

export function renderNonEmptyBlocks(
  blocks: CardBlock[],
  card: API.CardFragment,
  fields: CommonFields & Fields,
  canReadThread: boolean,
  canCreateThread: boolean,
  canUpdateCard: boolean,
  printing: boolean,
  onFieldPress: VoidFunction | undefined,
  workflowTemplateId?: string,
): ReactElement[] {
  if (blocks.length === 0) {
    return null;
  }
  return getBlocksToRender(
    blocks,
    card,
    fields,
    canReadThread,
    canCreateThread,
    canUpdateCard,
    printing,
  ).map((block, idx) => {
    const position = idx + 1;
    return renderBlock(block, card, fields, onFieldPress, position, workflowTemplateId);
  });
}

function textStyleAddedHeight(block: CardBlock): number {
  if (
    !!(block as FieldBlock).textStyle ||
    !!(block as TagsBlock).textStyle ||
    !!(block as DateRangeBlock).textStyle ||
    !!(block as DateInputBlock).textStyle
  ) {
    return 0;
  }

  return 2;
}

export function calculateCardHeight(
  blocks: CardBlock[],
  card: API.CardFragment,
  fields: CommonFields & Fields,
  canReadThread: boolean,
  canCreateThread: boolean,
  canUpdateCard: boolean,
  printing: boolean,
  renderContext: RenderContext,
): number {
  if (renderContext === 'calendar' && card.planLimitImposed) {
    return 74;
  }
  const blockHeights = blocks
    .filter(nonRedactedBlocks(card))
    .filter(nonEmptyBlocks(fields, canReadThread, canCreateThread, canUpdateCard, printing))
    .filter(block => evaluateSelector(block.selector, fields, defaultEvaluationContext))
    .map(block => {
      switch (block.type) {
        case 'field':
          switch (block.fieldType) {
            case 'assignee':
              if (!!card.assignees.length) {
                return renderContext === 'agenda' ? 30 : 22;
              } else {
                return renderContext === 'agenda' ? 30 : 22;
              }
            default:
              return (!!fields[block.name] ? 26 : 0) + textStyleAddedHeight(block);
          }
        case 'date-range':
          return renderContext === 'agenda' ? 30 : 22;
        case 'tags':
          return 34 + textStyleAddedHeight(block);
        default:
          return 0;
      }
    });
  const blockHeight = sum(blockHeights);
  const padding = renderContext === 'agenda' ? grid(3) : grid(1);
  return blockHeight + padding;
}
